import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { CatalogoService } from 'src/app/services/shared/catalogo.service';
import { GuiMsjService } from 'src/app/services/shared/gui-msj.service';
import { EntidadesKeys } from 'src/app/enum/entidades-keys.enum';
import { CatalogoCentrosPropios } from 'src/app/interfaces/interface-bakend/shared/catalogo/catalogo.centros-propios.interface';
import { MapComponent } from '@components/entidades/common/map/map.component';
import { environment } from '@env/environment';

@Component({
    selector: 'app-centro-propio-alta',
    templateUrl: './centro-propio-alta.component.html',
    styleUrls: ['./centro-propio-alta.component.scss']
})
export class CentroPropioAltaComponent implements OnInit, AfterViewInit {
    form: FormGroup;
    catalogo: CatalogoCentrosPropios;
    @ViewChild(MapComponent) mapComponent: MapComponent;

    // Propiedades para autocompletado de calle
    calleControl = new FormControl('');
    calleSuggestions: any[] = [];
    private calleSearchTerms = new Subject<string>();

    constructor(
        private router: Router,
        private catalogoService: CatalogoService,
        public guiMsjService: GuiMsjService
    ) {
        // Configurar búsqueda con debounce para la calle
        this.calleSearchTerms.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((term: string) => this.searchCalleSuggestions(term))
        ).subscribe(suggestions => {
            this.calleSuggestions = suggestions;
        });
    }

    ngOnInit(): void {
        this.getCatalogo();
        this.createForm();
    }

    ngAfterViewInit(): void {
        this.initMap();
        this.updateMap();
    }

    get centroNoValido() {
        return this.form.get('centro').invalid && this.form.get('centro').touched;
    }

    get calleNoValida() {
        return this.form.get('calle').invalid && this.form.get('calle').touched;
    }

    get alturaNoValida() {
        return this.form.get('altura').invalid && this.form.get('altura').touched;
    }

    async getCatalogo() {
        this.catalogo = await this.catalogoService.getFindByKey(EntidadesKeys.CENTROS_PROPIOS);
    }

    initMap() {
        const lat = this.form.get('latitud').value;
        const lng = this.form.get('longitud').value;
        if (lat != null && lng != null && !isNaN(Number(lat)) && !isNaN(Number(lng))) {
            this.mapComponent.setLocation(Number(lat), Number(lng));
        }
    }

    updateMap() {
        this.updatesCoordinates();
        const lat = this.form.get('latitud').value;
        const lng = this.form.get('longitud').value;
        if (lat != null && lng != null && !isNaN(Number(lat)) && !isNaN(Number(lng))) {
            this.mapComponent.setLocation(Number(lat), Number(lng));
        }
    }

    updatesCoordinates() {
        this.mapComponent.coordinatesChanged.subscribe((coordinates) => {
            console.log('Coordenadas actualizadas desde el mapa:', coordinates);
            this.form.patchValue({
                latitud: coordinates.latitud,
                longitud: coordinates.longitud
            });
        });
    }

    updateMarkerLocation() {
        const lat = this.form.get('latitud').value;
        const lng = this.form.get('longitud').value;
        if (this.mapComponent && lat != null && lng != null && !isNaN(Number(lat)) && !isNaN(Number(lng))) {
            this.mapComponent.setLocation(Number(lat), Number(lng));
        }
    }

    createForm() {
        this.form = new FormGroup({
            centro: new FormControl('', [
                Validators.required,
                Validators.minLength(3),
            ]),
            activa: new FormControl(true),
            telefono: new FormControl(''),
            calle: new FormControl('', [
                Validators.required,
                Validators.minLength(3),
                Validators.maxLength(50)
            ]),
            altura: new FormControl('', [
                Validators.required,
                Validators.minLength(1),
                Validators.maxLength(50)
            ]),
            latitud: new FormControl(-34.63104776379445, [
                Validators.pattern(/^-?\d*(\.\d+)?$/),
            ]),
            longitud: new FormControl(-58.46651124133983, [
                Validators.pattern(/^-?\d*(\.\d+)?$/),
            ])
        });
    }

    // --- Métodos para autocompletado de calle ---
    onCalleInput() {
        this.calleSearchTerms.next(this.calleControl.value);
    }

    private searchCalleSuggestions(query: string): Promise<any[]> {
        if (!query || query.length < 3) {
            return Promise.resolve([]);
        }
        const token = environment.mapBoxToken;
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${token}&autocomplete=true&types=address&country=ar&limit=5`;
        return fetch(url)
            .then(response => response.json())
            .then(data => data.features || [])
            .catch(error => {
                console.error('Error fetching suggestions', error);
                return [];
            });
    }

    onCalleSelected(event: MatAutocompleteSelectedEvent) {
        const feature = event.option.value;
        if (!feature) return;
        const calle = feature.place_name.split(',')[0].trim();
        this.form.patchValue({ calle });
        this.calleControl.setValue(calle);
        if (this.form.get('altura')?.value) {
            this.buscarDireccionCompleta();
        }
    }

    displayCalleFn(suggestion: any): string {
        if (typeof suggestion === 'string') return suggestion;
        return suggestion?.place_name?.split(',')[0] || '';
    }

    // --- Búsqueda completa (calle + altura) ---
    buscarDireccionCompleta() {
        const calle = this.form.get('calle')?.value;
        const altura = this.form.get('altura')?.value;
        if (!calle || !altura) {
            alert('Debe ingresar calle y altura para buscar.');
            return;
        }
        const direccionCompleta = `${calle} ${altura}, Argentina`;
        if (this.mapComponent) {
            this.mapComponent.searchFromAddress(direccionCompleta)
                .then(() => {
                    console.log('Mapa actualizado con la dirección:', direccionCompleta);
                })
                .catch(error => {
                    console.error('Error al buscar dirección:', error);
                    alert('No se pudo encontrar la dirección. Intente nuevamente.');
                });
        }
    }

    async setDataForma(): Promise<CatalogoCentrosPropios> {
        // Concatenar calle y altura para formar la dirección
        const direccionCompleta = `${this.form.value.calle} ${this.form.value.altura}`.trim();

        const data: CatalogoCentrosPropios = {
            key: null,
            concepto: this.form.value.centro,
            parent: this.catalogo._id,
            logico_1: this.form.value.activa,
            properties: {
                direccion: direccionCompleta,
                telefono: this.form.value.telefono,
                latitud: this.form.value.latitud,
                longitud: this.form.value.longitud
            }
        };
        return data;
    }

    async centroPropioCreate() {
        await this.guiMsjService.msjFormSubmit('Guardando');
        const data = await this.setDataForma();
        console.log('Datos a enviar al backend:', data);
        this.catalogoService.post(data).then(async () => {
            await this.guiMsjService.msjFormSubmit('DatosGuardados');
            this.router.navigateByUrl('/centros-propios-listado');
        }).catch((error) => {
            console.error('Error al guardar centro propio', error);
        });
    }
}